/**
 * App Component
 *
 * Root application component with routing and navigation.
 *
 * Routes:
 * - /app: Home page with scanner and price lookup flow (SPA entry)
 * - /offline-scanning: Offline barcode scanning tool (Silver/Gold only)
 * - /analytics: Analytics dashboard (Silver/Gold only)
 * - /login: Authentication page
 * - /profile: User profile and premium features
 * - /admin: Admin analytics dashboard (admin users only)
 *
 * Note: The root path `/` serves the marketing splash page (index.html)
 * and is NOT part of this React app. Never use `navigate("/")` or
 * `<Link to="/">` - always use `/app` for the scanner home.
 *
 * Premium features (Offline Scanning, Analytics) are only accessible
 * through the Profile page for Silver and Gold tier users.
 */

import React, { Suspense, lazy, useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToastProvider } from "./components/Toast";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Footer from "./components/Footer";
import CookieConsentModal from "./components/CookieConsentModal";
import { isISBN, normalizeISBN } from "./lib/isbn";
import { shouldShowConsentPopup } from "./lib/cookieConsent";
import "./styles/global.css";

// Lazy load pages for better initial load performance
const Home = lazy(() => import("./pages/Home"));
const OfflineScanning = lazy(() => import("./pages/OfflineScanning"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const Login = lazy(() => import("./pages/Login"));
const Profile = lazy(() => import("./pages/Profile"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));

// =============================================================================
// App Component with Router
// =============================================================================

export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ToastProvider position="top-right" maxToasts={5}>
          <AppLayout />
        </ToastProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

// =============================================================================
// App Layout with Navigation
// =============================================================================

const AppLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Cookie consent popup state
  const [showCookieConsent, setShowCookieConsent] = useState(false);

  // Show cookie consent popup after 6 seconds for new visitors
  useEffect(() => {
    // Don't show if user has already consented or has a tier
    if (!shouldShowConsentPopup()) {
      return;
    }

    const timer = setTimeout(() => {
      // Double-check in case auth state changed during the delay
      if (shouldShowConsentPopup()) {
        setShowCookieConsent(true);
      }
    }, 6000);

    return () => clearTimeout(timer);
  }, []);

  // Handle cookie consent completion
  const handleCookieConsentComplete = (accepted: boolean) => {
    setShowCookieConsent(false);
    if (accepted) {
      // User accepted - they now have Bronze tier
      // Page will update automatically when auth state changes
      console.info("Cookie consent accepted, Bronze tier enabled");
    } else {
      // User rejected - they stay as Guest
      console.info("Cookie consent rejected, staying as Guest");
    }
  };

  // Handle query parameters from splash page on initial mount only
  // This effect should only run once when the app first loads
  const hasHandledQueryParams = React.useRef(false);
  React.useEffect(() => {
    // Only process query params once on initial mount
    if (hasHandledQueryParams.current) return;
    hasHandledQueryParams.current = true;

    const params = new URLSearchParams(window.location.search);
    const page = params.get("page");
    const isbn = params.get("isbn");

    if (page === "login") {
      navigate("/login", { replace: true });
    } else if (isbn) {
      // Security: Validate and sanitize ISBN before using in state
      // This prevents XSS attacks via malicious query parameters
      const sanitizedIsbn = normalizeISBN(isbn);
      if (sanitizedIsbn && isISBN(sanitizedIsbn)) {
        // Navigate to /app with ISBN in URL for shareability
        // Home.tsx will read the ISBN from the URL query params
        navigate(`/app?isbn=${sanitizedIsbn}`, { replace: true });
      } else {
        // Invalid ISBN - navigate to home without the parameter
        console.warn("Invalid ISBN parameter ignored:", isbn.substring(0, 20));
        navigate("/app", { replace: true });
      }
    }
  }, [navigate]);

  // Hide navigation on pages with their own headers (Home/app, offline scanning, analytics, login, profile, admin)
  const hideNavigation =
    location.pathname === "/app" ||
    location.pathname === "/offline-scanning" ||
    location.pathname === "/analytics" ||
    location.pathname === "/login" ||
    location.pathname === "/profile" ||
    location.pathname === "/admin";

  // Hide footer on full-screen pages
  const hideFooter =
    location.pathname === "/offline-scanning" ||
    location.pathname === "/analytics" ||
    location.pathname === "/admin";

  // Show CTA section on most pages
  const showFooterCta = true;

  return (
    <div style={styles.container}>
      {/* Navigation */}
      {!hideNavigation && (
        <nav style={styles.nav}>
          <div style={styles.navContent}>
            <Link to="/app" style={styles.navLogo}>
              Secret Book Price
            </Link>

            <div style={styles.navLinks}>
              <Link
                to="/app"
                style={{
                  ...styles.navLink,
                  ...(location.pathname === "/app" ? styles.navLinkActive : {}),
                }}
              >
                Home
              </Link>
              <a href="/why.html" style={styles.navLink}>
                Why?
              </a>
            </div>
          </div>
        </nav>
      )}

      {/* Routes with Suspense for lazy loading */}
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<Navigate to="/app" replace />} />
          <Route path="/app" element={<Home />} />
          {/* Partner referral route - redirects to /app preserving query params */}
          <Route path="/search" element={<SearchRedirect />} />
          {/* Legacy route - redirect to profile where premium features are accessible */}
          <Route
            path="/bonus-features"
            element={<Navigate to="/profile" replace />}
          />
          <Route path="/offline-scanning" element={<OfflineScanning />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </Suspense>

      {/* Footer */}
      {!hideFooter && <Footer showCta={showFooterCta} />}

      {/* Cookie Consent Modal */}
      {showCookieConsent && (
        <CookieConsentModal onComplete={handleCookieConsentComplete} />
      )}
    </div>
  );
};

// =============================================================================
// Search Redirect Component (preserves query params for partner links)
// =============================================================================

const SearchRedirect: React.FC = () => {
  const location = useLocation();
  // Preserve query params like ?isbn=...&ref=...
  return <Navigate to={`/app${location.search}`} replace />;
};

// =============================================================================
// Loading Fallback Component
// =============================================================================

const LoadingFallback: React.FC = () => {
  return (
    <div style={styles.loadingContainer}>
      <div style={styles.spinner} />
      <p style={styles.loadingText}>Loading...</p>
    </div>
  );
};

// =============================================================================
// Styles
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  nav: {
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e5e7eb",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    position: "sticky",
    top: 0,
    zIndex: 1000,
  },
  navContent: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "12px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  navLogo: {
    fontSize: "20px",
    fontWeight: "700",
    fontFamily: '"Playfair Display", serif',
    color: "#2b1a45",
    textDecoration: "none",
    transition: "color 0.2s",
  },
  navLinks: {
    display: "flex",
    gap: "24px",
  },
  navLink: {
    fontSize: "15px",
    fontWeight: "500",
    color: "#6b7280",
    textDecoration: "none",
    transition: "color 0.2s",
    padding: "8px 12px",
    borderRadius: "6px",
  },
  navLinkActive: {
    color: "#6a49a8",
    backgroundColor: "#eee9fa",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "50vh",
    padding: "48px 24px",
  },
  spinner: {
    width: "48px",
    height: "48px",
    border: "4px solid #d8cff3",
    borderTopColor: "#6a49a8",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },
  loadingText: {
    marginTop: "16px",
    fontSize: "16px",
    color: "#6b7280",
    fontWeight: "500",
  },
};

export default App;
